import { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import AutoComplete from "../elements/AutoComplete";
import Input from "../elements/Input";
import EditorToolbar from "./EditorToolbar";
import Dialog_YesNo from "../ui/Dialog_YesNo";

const AdjustmentEditor = ({
  adjustment,
  users,
  projects,
  adjustmentTypes,
  payPeriodId,
  currentUserId,
  onSave,
  onClose,
}) => {
  const activeAdjustmentTypes = Array.isArray(adjustmentTypes)
    ? adjustmentTypes.filter((type) => type?.isActive !== false)
    : [];

  const defaultType = activeAdjustmentTypes[0]?.key || "";

  const [data, setData] = useState({
    userId: null,
    projectId: null,
    type: defaultType,
    amount: "",
    status: "pending",
    notes: "",
  });

  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (adjustment) {
      setData({
        userId: adjustment.user?.id || adjustment.userId,
        projectId: adjustment.project?.id || adjustment.projectId || null,
        type: adjustment.type || defaultType,
        amount: adjustment.amount?.toString() || "",
        status: adjustment.status || "pending",
        notes: adjustment.notes || "",
      });
      return;
    }

    setData((current) => ({
      ...current,
      type: current.type || defaultType,
    }));
  }, [adjustment, defaultType]);

  const handleChange = (field, value) => {
    setData((current) => ({ ...current, [field]: value }));
    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors((current) => ({ ...current, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!data.userId) {
      newErrors.userId = "User is required";
    }

    if (!data.type) {
      newErrors.type = "Type is required";
    }

    if (!data.projectId) {
      newErrors.projectId = "Project is required";
    }

    if (data.amount === "" || data.amount === null) {
      newErrors.amount = "Amount is required";
    } else if (isNaN(parseFloat(data.amount))) {
      newErrors.amount = "Amount must be a valid number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClick = () => {
    if (!validateForm()) {
      return;
    }

    // Check if amount > $100 and show confirmation
    const amount = parseFloat(data.amount);
    if (Math.abs(amount) > 100) {
      setShowConfirm(true);
    } else {
      performSave();
    }
  };

  const performSave = async () => {
    const amount = parseFloat(data.amount);
    const adjustmentData = {
      ...(adjustment && { id: adjustment.id }),
      userId: data.userId,
      projectId: data.projectId,
      payPeriodId,
      createdBy: currentUserId,
      type: data.type,
      amount,
      status: data.status,
      notes: data.notes,
    };

    await onSave(adjustmentData);
    setShowConfirm(false);
  };

  return (
    <>
      <Box width="100%">
        <EditorToolbar onSave={handleSaveClick} />

        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          padding="16px"
        >
          {/* User Selection */}
          <AutoComplete
            id="userId"
            label="User"
            value={data.userId}
            items={users}
            onChange={(userId) => handleChange("userId", userId)}
            width="100%"
            disabled={!!adjustment}
          />
          {errors.userId && (
            <Box sx={{ color: "red", fontSize: "12px" }}>{errors.userId}</Box>
          )}

          {/* Project Selection */}
          <AutoComplete
            id="projectId"
            label="Project"
            value={data.projectId}
            items={projects}
            onChange={(projectId) => handleChange("projectId", projectId)}
            width="100%"
          />
          {errors.projectId && (
            <Box sx={{ color: "red", fontSize: "12px" }}>
              {errors.projectId}
            </Box>
          )}

          {/* Adjustment Type - MUI Select */}
          <FormControl
            fullWidth
            size="small"
          >
            <InputLabel>Adjustment Type</InputLabel>
            <Select
              value={data.type}
              onChange={(e) => handleChange("type", e.target.value)}
              label="Adjustment Type"
            >
              {activeAdjustmentTypes.map((type) => (
                <MenuItem
                  key={type.id || type.key}
                  value={type.key}
                >
                  {type.label || type.key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {errors.type && (
            <Box sx={{ color: "red", fontSize: "12px" }}>{errors.type}</Box>
          )}

          {/* Amount */}
          <Box>
            <Input
              type="number"
              label="Amount"
              value={data.amount}
              onChange={(value) => handleChange("amount", value)}
              placeholder="Can be positive or negative"
            />
            {errors.amount && (
              <Box sx={{ color: "red", fontSize: "12px" }}>{errors.amount}</Box>
            )}
          </Box>

          {/* Status - MUI Select */}
          <FormControl
            fullWidth
            size="small"
          >
            <InputLabel>Status</InputLabel>
            <Select
              value={data.status}
              onChange={(e) => handleChange("status", e.target.value)}
              label="Status"
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
            </Select>
          </FormControl>

          {/* Notes */}
          <Box>
            <Input
              type="textarea"
              label="Notes"
              value={data.notes}
              onChange={(value) => handleChange("notes", value)}
              placeholder="Optional admin notes"
              multiline={true}
              rows={4}
            />
          </Box>
        </Box>
      </Box>

      {/* Confirmation Dialog for amounts > $100 */}
      <Dialog_YesNo
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        title="Confirm Large Adjustment"
        message={`This adjustment amount is $${Math.abs(
          parseFloat(data.amount) || 0,
        ).toFixed(2)}, which exceeds $100. Are you sure you want to proceed?`}
        onYes={performSave}
        onNo={() => setShowConfirm(false)}
      />
    </>
  );
};

export default AdjustmentEditor;
